import React, { useState, useMemo, type SetStateAction } from "react";
import {
    Alert,
    CardContent,
    FormControl,
    FormControlLabel,
    FormLabel,
    InputLabel,
    MenuItem,
    Radio,
    RadioGroup,
    Select,
    Stack,
    Typography,
} from "@mui/material";
import { useTranslation } from "shared/hooks/useTranslation";
import { ExpandableCard } from "shared/ui/ComposedCards";
import MoneyInput from "shared/ui/MoneyInput";
import type { LiquidityPool } from "features/wallet/api/Wallet";
import FormDialog from "shared/ui/FormDialog";
import FormActions from "shared/ui/FormActions";
import { FORM_FIELD_GAP } from "shared/ui/formConstants";
import { formatCurrencyAmount, languageToLocale } from "shared/money/balances";

/** When true: 1 source = rate target (targetAmount = sourceAmount * rate). When false: 1 target = rate source (targetAmount = sourceAmount / rate). */
export type RateFormat = "sourcePerTarget" | "targetPerSource";

export interface ExchangeCardProps {
    pools: LiquidityPool[];
    onSubmit: (data: {
        sourcePoolId: string;
        targetPoolId: string;
        sourceAmount: number;
        rate: number;
        rateMultiplies: boolean;
    }) => Promise<{ success: boolean; error?: string }>;
    open?: boolean;
    onClose?: () => void;
    variant?: "inline" | "dialog";
    mutationsDisabled?: boolean;
}

const ExchangeCard: React.FC<ExchangeCardProps> = ({ pools, onSubmit, open, onClose, variant = "inline", mutationsDisabled = false }) => {
    const { t, i18n } = useTranslation();
    const [internalIsOpen, setInternalIsOpen] = useState(false);
    const isOpen = open ?? internalIsOpen;
    const setIsOpen = onClose
        ? (next: SetStateAction<boolean>) => {
            const resolved = typeof next === "function" ? next(isOpen) : next;
            if (!resolved) onClose();
        }
        : setInternalIsOpen;
    const [sourcePoolId, setSourcePoolId] = useState("");
    const [targetPoolId, setTargetPoolId] = useState("");
    const [sourceAmount, setSourceAmount] = useState("");
    const [rate, setRate] = useState("");
    const [rateFormat, setRateFormat] = useState<RateFormat>("targetPerSource");
    const [error, setError] = useState<string | null>(null);
    const [submitting, setSubmitting] = useState(false);

    const differentCurrencyPools = useMemo(() => {
        if (!sourcePoolId) return [];
        const source = pools.find((p) => p._id === sourcePoolId);
        if (!source) return [];
        return pools.filter((p) => p.currency !== source.currency);
    }, [pools, sourcePoolId]);

    const sourcePool = useMemo(() => pools.find((p) => p._id === sourcePoolId), [pools, sourcePoolId]);
    const targetPool = useMemo(() => pools.find((p) => p._id === targetPoolId), [pools, targetPoolId]);

    const srcAmt = parseFloat(sourceAmount);
    const rateVal = parseFloat(rate);
    const rateMultiplies = rateFormat === "sourcePerTarget";
    const targetAmount =
        Number.isFinite(srcAmt) && Number.isFinite(rateVal) && rateVal > 0
            ? rateMultiplies
                ? srcAmt * rateVal
                : srcAmt / rateVal
            : null;
    const insufficientBalance = Boolean(sourcePool && Number.isFinite(srcAmt) && srcAmt > sourcePool.currentBalance);
    const locale = languageToLocale(i18n.language);

    const canExchange = useMemo(() => {
        const currencies = new Set(pools.map((p) => p.currency));
        return pools.length >= 2 && currencies.size >= 2;
    }, [pools]);

    const handleReset = () => {
        setSourcePoolId("");
        setTargetPoolId("");
        setSourceAmount("");
        setRate("");
        setRateFormat("targetPerSource");
        setError(null);
    };

    const handleCancel = () => {
        if (submitting) return;
        handleReset();
        setIsOpen(false);
    };

    const handleSourceChange = (id: string) => {
        setSourcePoolId(id);
        setTargetPoolId("");
    };

    const handleSubmit = async () => {
        if (submitting) return;
        if (!sourcePoolId || !targetPoolId) {
            setError(t("wallet.selectBothPools"));
            return;
        }
        if (sourcePoolId === targetPoolId) {
            setError(t("wallet.samePoolError"));
            return;
        }
        const amt = parseFloat(sourceAmount);
        if (isNaN(amt) || amt <= 0) {
            setError(t("wallet.invalidAmount"));
            return;
        }
        const r = parseFloat(rate);
        if (!Number.isFinite(r) || r <= 0) {
            setError(t("wallet.rateMustBePositive"));
            return;
        }
        setError(null);
        setSubmitting(true);
        try {
            const result = await onSubmit({
                sourcePoolId,
                targetPoolId,
                sourceAmount: amt,
                rate: r,
                rateMultiplies,
            });
            if (result.success) {
                handleReset();
                setIsOpen(false);
            } else {
                setError(result.error ?? t("common.error"));
            }
        } finally {
            setSubmitting(false);
        }
    };

    const canSubmit = canExchange
        && Boolean(sourcePoolId && targetPoolId && sourceAmount && rate)
        && Number.isFinite(parseFloat(sourceAmount))
        && parseFloat(sourceAmount) > 0
        && Number.isFinite(parseFloat(rate))
        && Number.isFinite(parseFloat(rate))
        && parseFloat(rate) > 0;

    const inner = (
        <Stack spacing={FORM_FIELD_GAP}>
            <Alert severity="info">
                {t("wallet.exchangeRecordHint")}
            </Alert>
            {!canExchange ? (
                <Typography variant="body2" color="text.secondary">
                    {t("wallet.exchangeRequiresDifferentCurrencies")}
                </Typography>
            ) : null}
            {error ? <Alert severity="error">{error}</Alert> : null}
            <FormControl fullWidth size="small" disabled={!canExchange}>
                <InputLabel>{t("wallet.sourceAccount")}</InputLabel>
                <Select
                    value={sourcePoolId}
                    label={t("wallet.sourceAccount")}
                    onChange={(e) => handleSourceChange(e.target.value)}
                >
                    {pools.map((p) => (
                        <MenuItem key={p._id} value={p._id}>
                            {p.name} ({p.currency})
                        </MenuItem>
                    ))}
                </Select>
            </FormControl>
            <FormControl fullWidth size="small">
                <InputLabel>{t("wallet.destinationAccount")}</InputLabel>
                <Select
                    value={targetPoolId}
                    label={t("wallet.destinationAccount")}
                    onChange={(e) => setTargetPoolId(e.target.value)}
                    disabled={!sourcePoolId || !canExchange}
                >
                    {differentCurrencyPools.map((p) => (
                        <MenuItem key={p._id} value={p._id}>
                            {p.name} ({p.currency})
                        </MenuItem>
                    ))}
                </Select>
            </FormControl>
            <MoneyInput
                label={t("forms.amount")}
                value={sourceAmount}
                onValueChange={setSourceAmount}
                fullWidth
                size="small"
                disabled={!sourcePoolId || !canExchange}
                helperText={sourcePool ? `${sourcePool.currency}` : undefined}
            />
            {sourcePool && targetPool ? (
                <>
                    <FormControl component="fieldset" size="small" fullWidth>
                        <FormLabel component="legend">{t("wallet.rateFormat")}</FormLabel>
                        <RadioGroup
                            row
                            value={rateFormat}
                            onChange={(e) => setRateFormat(e.target.value as RateFormat)}
                        >
                            <FormControlLabel
                                value="targetPerSource"
                                control={<Radio size="small" />}
                                label={t("wallet.rateFormatTargetSource", {
                                    target: targetPool.currency,
                                    source: sourcePool.currency,
                                })}
                            />
                            <FormControlLabel
                                value="sourcePerTarget"
                                control={<Radio size="small" />}
                                label={t("wallet.rateFormatSourceTarget", {
                                    source: sourcePool.currency,
                                    target: targetPool.currency,
                                })}
                            />
                        </RadioGroup>
                    </FormControl>
                    <MoneyInput
                        label={t("wallet.rateValue")}
                        value={rate}
                        onValueChange={setRate}
                        decimals={8}
                        fullWidth
                        size="small"
                        disabled={!canExchange}
                        helperText={sourcePool && targetPool
                            ? rateFormat === "targetPerSource"
                                ? `1 ${sourcePool.currency} = ${rate || "…"} ${targetPool.currency}`
                                : `1 ${targetPool.currency} = ${rate || "…"} ${sourcePool.currency}`
                            : undefined}
                    />
                </>
            ) : null}
            {targetAmount != null && Number.isFinite(targetAmount) && targetAmount >= 0.000001 && sourcePool && targetPool ? (
                <Stack spacing={0.5} sx={{ p: 1.5, borderRadius: 2, bgcolor: "action.hover" }}>
                    <Typography variant="subtitle2" sx={{ fontWeight: 700 }}>{t("wallet.resultingBalances")}</Typography>
                    <Typography variant="body2" color="text.secondary">
                        {t("wallet.balanceAfterMovement", {
                            name: sourcePool.name,
                            before: formatCurrencyAmount(sourcePool.currentBalance, sourcePool.currency, locale),
                            after: formatCurrencyAmount(sourcePool.currentBalance - srcAmt, sourcePool.currency, locale),
                        })}
                    </Typography>
                    <Typography variant="body2" color="text.secondary">
                        {t("wallet.balanceAfterMovement", {
                            name: targetPool.name,
                            before: formatCurrencyAmount(targetPool.currentBalance, targetPool.currency, locale),
                            after: formatCurrencyAmount(targetPool.currentBalance + targetAmount, targetPool.currency, locale),
                        })}
                    </Typography>
                    {insufficientBalance && <Alert severity="error">{t("wallet.insufficientExchangePreview")}</Alert>}
                </Stack>
            ) : null}
            <FormActions
                submitLabel={t("wallet.saveRecord")}
                submitButtonType="button"
                onSubmit={handleSubmit}
                onCancel={handleCancel}
                submitDisabled={!canSubmit || mutationsDisabled || insufficientBalance || !Number.isFinite(targetAmount ?? NaN)}
                submitLoading={submitting}
            />
        </Stack>
    );

    return (
        variant === "dialog" ? (
            <FormDialog
                open={isOpen}
                onClose={() => {
                    if (!submitting) handleCancel();
                }}
                title={t("wallet.exchange")}
                disabled={submitting}
            >
                {inner}
            </FormDialog>
        ) : (
            <ExpandableCard title={t("wallet.exchange")} open={isOpen} onOpenChange={setIsOpen}>{<CardContent>{inner}</CardContent>}</ExpandableCard>
        )
    );
};

export default ExchangeCard;
